<template>
  <div
    class="relative bg-[white] border rounded-lg transition-all duration-200 flex flex-col cursor-pointer print-card"
    :class="cardBorderClass"
    @click="openPreview"
  >
    <div class="relative p-2 pb-0">
      <div class="rounded-lg overflow-hidden">
        <PrintPreviewModal
          ref="previewModalRef"
          :content="{
            id: content.id,
            title: content.title,
            description: content.description,
            mediumDescription: content.mediumDescription,
            dimensions: content.dimensions,
            imageUrl: content.imageUrl,
            previewUrl: content.previewUrl,
            customPreviewUrl: content.customPreviewUrl,
            fileUrl: content.fileUrl,
            printProductId: content.printProductId,
            productType: content.productType,
            isCustomizable: content.isCustomizable,
            quadrants: content.quadrants,
            printProduct: content.printProduct,
          }"
          @add-to-cart="handleModalAddToCart"
        />
      </div>
    </div>

    <div class="p-4 flex-1 flex flex-col">
      <div class="mb-4">
        <h3 class="text-lg font-bold truncate" :title="content.title">
          {{ truncatedTitle }}
        </h3>
        <p v-if="content.productType === 'PRINT'" class="text-base text-[gray]">
          {{ content.mediumDescription }}
        </p>
        <p v-if="content.productType === 'PRINT'" class="text-base text-[gray]">
          ({{ content.dimensions }})
        </p>
        <p
          v-if="content.productType === 'PRODUCT'"
          class="text-base text-[gray]"
        >
          {{ content.description }}
        </p>
      </div>

      <div class="flex-grow"></div>

      <div class="h-6 mb-2">
        <div v-if="dateDisplay" class="flex items-center gap-1">
          <Icon :icon="CalendarSvg" class="w-5 h-5 text-[gray]" />
          <div class="text-2xs text-[gray]">
            {{ dateDisplay }}
          </div>
        </div>
        <div v-if="content.isCustomizable" class="absolute bottom-2 left-2">
          <Chip
            :size="ChipSize.SM"
            :variant="ChipVariant.FILLED"
            :icon="SparkleIcon"
            :color="ChipColor.SECONDARY"
            class="!bg-surface !rounded-sm !text-on-surface"
          >
            {{ $t('content.personalizable') }}
          </Chip>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { Icon } from '~/components/icon';
import { Chip, ChipColor, ChipSize, ChipVariant } from '~/components/chip';
import { PrintPreviewModal } from './_components/print-preview-modal';
import CalendarSvg from '~/assets/icons/calendar.svg?component';
import { usePrintStore } from '~/store/print';
import type { PriceTier } from '~/api/print';
import SparkleIcon from '~/assets/icons/sparkle.svg?component';

type Props = {
  content: {
    id: number;
    title: string;
    description: string;
    mediumDescription: string;
    contentTitle: string;
    dimensions: string;
    imageUrl: string | null;
    previewUrl: string | null;
    customPreviewUrl: string | null;
    fileUrl: string | null;
    printProductId: number;
    isCustomizable?: boolean;
    productType: 'PRINT' | 'PRODUCT';
    printProduct: {
      contentFilePrintId: number;
      priceTiers: PriceTier[];
      priceOverride?: number | null;
      availableQuantity: number;
    };
    startDate?: string | null;
    endDate?: string | null;
    quadrants: any[];
  };
};

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'update:quantities', value: Record<string, number>): void;
}>();

const printStore = usePrintStore();

const selectedQuantity = ref<number | null>(null);

const previewModalRef = ref<any>(null);
const openPreview = () => {
  previewModalRef.value?.openModal?.();
};

const cardBorderClass = computed<string>(() =>
  selectedQuantity.value && selectedQuantity.value > 0
    ? 'border-[primary] shadow-[0_0_0_1px_#417dff]'
    : 'border-gray',
);

const handleModalAddToCart = (data: {
  quantity: number;
  isCustomizable: boolean;
  contentId?: number;
}) => {
  selectedQuantity.value = data.quantity;

  if (!data.isCustomizable) {
    emit('update:quantities', {
      [props.content.printProduct.contentFilePrintId.toString()]: data.quantity,
    });
  }
};

watch(
  () =>
    printStore.quantities[
      props.content.printProduct.contentFilePrintId.toString()
    ],
  (newQuantity) => {
    const qty = typeof newQuantity === 'number' ? newQuantity : 0;
    selectedQuantity.value = qty > 0 ? qty : null;
  },
);

onMounted(() => {
  printStore.initCurrency();
  const itemKey = props.content.printProduct.contentFilePrintId.toString();
  const storeQuantity = printStore.quantities[itemKey] ?? 0;
  selectedQuantity.value = storeQuantity > 0 ? storeQuantity : null;
});

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
  });
};

const dateDisplay = computed(() => {
  const { startDate, endDate } = props.content;

  if (endDate) {
    return `Available until ${formatDate(endDate)}`;
  }

  return '';
});

const truncatedTitle = computed(() => {
  const maxLength = 25;
  return props.content.title.length > maxLength
    ? `${props.content.title.slice(0, Math.max(0, maxLength))}...`
    : props.content.title;
});
</script>

<style scoped>
@reference "../../../../assets/css/main.css";

.print-card:hover {
  transform: translateY(-1px);
}

:deep(.print-select) {
  .input-area {
    @apply rounded-lg!;
    @apply bg-[white]!;
    @apply border-[gray]!;
    @apply w-full!;
  }

  input {
    @apply bg-[white]!;
    @apply placeholder-[gray]!;
    @apply text-[gray]!;
    @apply w-full!;
  }
}
</style>
